/**
 * index.tsx
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */

import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';
import { shouldPolyfill as shouldPolyfillPluralRules } from '@formatjs/intl-pluralrules/should-polyfill';
import { shouldPolyfill as shouldPolyfillGetCanonicalLocales } from '@formatjs/intl-getcanonicallocales/should-polyfill';
import { shouldPolyfill as shouldPolyfillLocale } from '@formatjs/intl-locale/should-polyfill';
import { shouldPolyfill as shouldPolyfillRelativeTimeFormat } from '@formatjs/intl-relativetimeformat/should-polyfill';

import * as React from 'react';
import * as ReactDOMClient from 'react-dom/client';
import { Provider } from 'react-redux';
import * as Sentry from '@sentry/react';

// Use consistent styling
import 'modern-normalize/modern-normalize.css';
import 'fomantic-ui-less/semantic.less';

// Load the favicon, the manifest.json file and the .htaccess file
/* eslint-disable import/no-unresolved, import/extensions, import/no-webpack-loader-syntax */
import '!file-loader?name=[name].[ext]!./images/favicon.ico';
/* eslint-enable import/no-unresolved, import/extensions, import/no-webpack-loader-syntax */


// Import i18n messages
import { translationMessages } from './i18n';

import reportWebVitals from 'reportWebVitals';

// Create redux store with history
const initialState = {};
const MOUNT_NODE = document.getElementById('root') as HTMLElement;

const fetchConfig = async () => new Promise<void>((resolve) => {
  const req = new XMLHttpRequest();
  req.responseType = 'json';
  req.open('GET', `/voeux/student/config.json?cb=${new Date().getTime()}`, true);
  req.onload = () => {
    const data = req.response;
    Object.keys(data).forEach((key) => {
      window[key] = data[key];
    });
    resolve();
  };
  req.send(null);
});

const sentryReplay = new Sentry.Replay();

const render = async messages => {
  await fetchConfig();

  if (window.SENTRY_DSN) {
    Sentry.init({
      dsn: window.SENTRY_DSN,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 1.0,
      integrations: [
        sentryReplay,
      ],
      beforeBreadcrumb(breadcrumb, hint) {
        if (breadcrumb.category !== 'xhr') {
          return breadcrumb;
        }
        if (!breadcrumb.data) {
          breadcrumb.data = {};
        }
        if (typeof hint?.input === 'string' && hint?.input.length < 1000) {
          breadcrumb.data.requestBody = hint.input;
        }
        if (typeof hint?.xhr?.response === 'string' && hint.xhr.response.length < 500) {
          breadcrumb.data.responseBody = hint.xhr.response;
        }
        return breadcrumb;
      }
    });
  }
  const { configureAppStore } = await import('./store/configureStore');
  const { default: LanguageProvider } = await import('containers/LanguageProvider');
  const { default: App } = await import('containers/App');
  const store = configureAppStore(initialState);
  const root = ReactDOMClient.createRoot(MOUNT_NODE);
  root.render(
    <Provider store={store}>
      <LanguageProvider messages={messages}>
        <App />
      </LanguageProvider>
    </Provider>
  );
};

// Chunked polyfill for browsers without Intl support
(async () => {
  if (shouldPolyfillGetCanonicalLocales()) {
    await import('@formatjs/intl-getcanonicallocales/polyfill');
  }
  if (shouldPolyfillLocale()) {
    await import('@formatjs/intl-locale/polyfill');
  }
  if (shouldPolyfillPluralRules()) {
    await import('@formatjs/intl-pluralrules');
    await import('@formatjs/intl-pluralrules/locale-data/en');
    await import('@formatjs/intl-pluralrules/locale-data/fr');
  }
  if (shouldPolyfillRelativeTimeFormat()) {
    await import('@formatjs/intl-relativetimeformat');
    await import('@formatjs/intl-relativetimeformat/locale-data/en');
    await import('@formatjs/intl-relativetimeformat/locale-data/fr');
  }
  render(translationMessages);
})();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
